// LES EN-TETES DE LA RANGEE 3 D'UNE FICHE (chantier `fiche-suivi`,
// 27/10/2026) : la prop `etapes` de `ScreenLayout`, une liste d'`EtapesDuGabarit`
// (`fiche-contrat.ts`), rendus cote a cote, alignes a gauche juste apres la
// puce de sortie (recette du 27/10). Deux modes, choisis par la porte champ par
// champ (`docs/socle/fiche-suivi.md`, section 2) : le mode FLUX, ici, les
// etapes en chevrons pour un parcours ordonne ; le mode SELECTION, les
// etiquettes de `SelectionDeFiche.tsx`. Le dessin est celui de l'atelier
// (`docs/maquettes/atelier-2610/README.md`, sections 2, 3 et 5).
//
// REGLE COMMUNE : une etape ne change JAMAIS de largeur avec son etat, mais son
// texte PEUT bouger (retour du fondateur du 02/11). La case prend la largeur de
// SON libelle (entre un minimum et un maximum, retour du 28/10) et la graisse du
// texte est la meme dans les trois etats : la largeur ne depend que du libelle.
// Sans marque (a venir, courante), le libelle est centre dans la partie utile de
// la case, hors pointe et encoche. Avec une marque (la coche d'une etape faite,
// ou le cadenas d'une valeur verrouillee, qui prend sa place), le groupe
// « marque + libelle » est centre de la meme facon : la marque est suspendue a
// gauche du libelle, hors du flux, et le libelle glisse d'une demi-place de
// coche vers la droite, d'un mouvement court (aucun saut).
import type { CSSProperties, JSX } from 'react';
import { cn } from '../lib/cn.js';
import { useEveil } from '../lib/useEveil.js';
import { IconCheck, IconPadlockSquare1 } from '../icons/index.js';
import type { EtapesDuGabarit, ValeurDEtape } from './fiche-contrat.js';
import type { VerrouDeLaPiste } from './SelectionDeFiche.js';
import { SelectionDeFiche } from './SelectionDeFiche.js';
import { useGroupeDeChoix, ZoneQuiDefile } from './ZoneQuiDefile.js';

/** L'etat d'une etape au regard de la courante. */
export type EtatDEtape = 'faite' | 'courante' | 'a-venir';

/**
 * L'ETAT D'UNE ETAPE : avant la courante, faite ; la courante ; apres, a venir.
 * Sans valeur courante (`-1`), tout est a venir : rien n'est fait tant que la
 * fiche n'a pas d'etape.
 */
export function etatDEtape(index: number, courante: number): EtatDEtape {
  if (courante < 0) return 'a-venir';
  return index < courante ? 'faite' : index === courante ? 'courante' : 'a-venir';
}

/** L'index de la valeur courante dans les valeurs ORDONNEES, `-1` sans valeur ou valeur inconnue. */
export function indexDeLaValeur(valeurs: readonly ValeurDEtape[], valeur: string | null): number {
  return valeur === null ? -1 : valeurs.findIndex((candidate) => candidate.id === valeur);
}

/**
 * LA RAISON D'UNE PISTE INERTE (G16) : celle que l'ecran donne, sinon le
 * constat. Nulle : la piste choisit.
 */
export function raisonDesEtapes(etapes: EtapesDuGabarit): string | null {
  if (etapes.raison != null) return etapes.raison;
  return etapes.onChoisir == null ? 'Modification indisponible ici' : null;
}

/**
 * LE TITRE D'UNE VALEUR VERROUILLEE : son nom et la raison, sur le bouton
 * lui-meme (G16, forme 1 : le controle reste vivant en `aria-disabled`).
 */
export const titreDuVerrou = (valeur: ValeurDEtape, raison: string): string => `${valeur.libelle} : ${raison}`;

/* La pointe d'un chevron, et le blanc entre deux chevrons, mesure le long de
   la diagonale. */
const POINTE = '0.625rem';
const JOUR = '0.1875rem';

/* LA LARGEUR D'UNE CASE suit son libelle (retour du fondateur du 28/10 : « En
   salle d'attente doit pouvoir etre lu en entier »), bornee des deux cotes.
   CASE_MIN : un nom court (« Payee ») ne fait pas une case ridicule, on clique
   toujours une vraie case. LIBELLE_MAX : la largeur du TEXTE au-dela de
   laquelle il est coupe (`truncate`) ; calibree pour qu'« En salle d'attente »
   plus 3 ou 4 caracteres tienne en entier, pas davantage (a la taille de la
   piste, 0.8125rem : 22 caracteres tiennent, 26 sont coupes). */
const CASE_MIN = '6rem';
const LIBELLE_MAX = '8.75rem';

/* LES MARGES INTERIEURES, les memes pour toutes les cases. MARGE : le blanc de
   chaque cote du groupe « marque + libelle », mesure depuis le corps du chevron
   (hors pointe et encoche). DEMI_COCHE : la moitie de la place de la marque
   (0.75rem d'icone et 0.375rem de jour avec le texte, soit 1.125rem), reservee
   SYMETRIQUEMENT a gauche et a droite dans les trois etats (retour du fondateur
   du 02/11) : la zone de contenu est ainsi centree sur la partie utile, le
   libelle seul y est au milieu, et le groupe « marque + libelle », qui deborde
   de DEMI_COCHE de chaque cote dans ce blanc, l'est aussi une fois le libelle
   glisse de DEMI_COCHE. */
const MARGE = '0.75rem';
const DEMI_COCHE = '0.5625rem';

/* Pointe a droite, encoche a gauche. La premiere n'a pas d'encoche, la
   derniere pas de pointe : la piste commence et finit a plat. */
function formeChevron(index: number, total: number): CSSProperties {
  const premier = index === 0;
  const dernier = index === total - 1;
  const droite = dernier ? '100% 0, 100% 100%' : `calc(100% - ${POINTE}) 0, 100% 50%, calc(100% - ${POINTE}) 100%`;
  const gauche = premier ? '0 100%' : `0 100%, ${POINTE} 50%`;
  return {
    transitionDuration: 'var(--dur-base)',
    transitionTimingFunction: 'var(--ease-standard)',
    clipPath: `polygon(0 0, ${droite}, ${gauche})`,
    paddingLeft: premier ? `calc(${MARGE} + ${DEMI_COCHE})` : `calc(${POINTE} + ${MARGE} + ${DEMI_COCHE})`,
    paddingRight: dernier ? `calc(${MARGE} + ${DEMI_COCHE})` : `calc(${POINTE} + ${MARGE} + ${DEMI_COCHE})`,
    ...(premier ? {} : { marginLeft: `calc(${JOUR} - ${POINTE})` }),
    minWidth: CASE_MIN,
  };
}

/* LES TROIS ETATS (atelier, section 2). Inerte, le survol ne fonce plus : il
   promettrait un clic qui ne fera rien. */
const COULEURS: Record<EtatDEtape, { fond: string; survol: string }> = {
  faite: { fond: 'bg-accent-tint text-accent-ink', survol: 'hover:bg-accent-tint-border focus-visible:bg-accent-tint-border' },
  courante: { fond: 'bg-accent text-accent-label', survol: 'hover:bg-accent-hover focus-visible:bg-accent-hover' },
  'a-venir': {
    fond: 'bg-surface-3 text-ink-500',
    survol: 'hover:bg-border hover:text-ink-900 focus-visible:bg-border focus-visible:text-ink-900',
  },
};

/**
 * LA PISTE DES ETAPES. Chaque case a la largeur de son nom, la meme dans les
 * trois etats ; la piste
 * grandit avec le nombre d'etapes et DEFILE quand il n'y a plus de place
 * (`ZoneQuiDefile`), l'etape courante toujours visible.
 */
function PisteDEtapes({
  libelle,
  valeurs,
  courante,
  choisir,
  verrou,
}: {
  libelle: string;
  valeurs: readonly ValeurDEtape[];
  courante: number;
  choisir: ((index: number) => void) | null;
  verrou?: VerrouDeLaPiste;
}): JSX.Element {
  const total = valeurs.length;
  const groupe = useGroupeDeChoix(courante, total, choisir, verrou?.verrouillee);
  return (
    <ZoneQuiDefile cle={`${courante}-${total}`}>
      <div role="radiogroup" aria-label={libelle} onKeyDown={groupe.onKeyDown} className="flex w-fit">
        {valeurs.map((valeur, index) => {
          const etat = etatDEtape(index, courante);
          /* UNE VALEUR VERROUILLEE (jamais la courante) : le cadenas prend la place de la coche, la raison est au survol. */
          const verrouillee = choisir != null && index !== courante && verrou?.verrouillee(index) === true;
          const marque = etat === 'faite' || verrouillee;
          return (
            <button
              key={valeur.id}
              {...groupe.radio(index)}
              {...(choisir == null ? {} : { title: verrouillee ? titreDuVerrou(valeur, verrou!.raison(index)) : valeur.libelle })}
              {...(verrouillee ? { 'aria-description': verrou!.raison(index) } : {})}
              style={{ ...formeChevron(index, total), transitionProperty: 'background-color, color' }}
              className={cn(
                'flex h-8 shrink-0 items-center justify-center whitespace-nowrap text-[0.8125rem] font-medium focus-visible:outline-none motion-reduce:transition-none',
                choisir == null ? 'cursor-default' : verrouillee ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                index === 0 && 'rounded-l-sm',
                index === total - 1 && 'rounded-r-sm',
                COULEURS[etat].fond,
                choisir != null && !verrouillee && COULEURS[etat].survol,
              )}
            >
              {/* La marque (coche ou cadenas) est SUSPENDUE a gauche du libelle,
                  hors du flux (0.375rem de jour et 0.75rem d'icone, soit deux
                  fois `DEMI_COCHE`) : elle suit le libelle, qui glisse de
                  `DEMI_COCHE` vers la droite quand elle est la (retour du
                  fondateur du 02/11), le groupe se trouvant alors centre.
                  `truncate` et `LIBELLE_MAX` sur le TEXTE seulement : poses sur
                  l'enveloppe, son `overflow: hidden` rognerait la marque. La
                  PROPRIETE de la transition est une classe, jamais un style :
                  en ligne, elle l'emporterait sur `motion-reduce`. */}
              <span
                className="relative flex min-w-0 transition-transform motion-reduce:transition-none"
                style={{
                  transform: marque ? `translateX(${DEMI_COCHE})` : 'translateX(0)',
                  transitionDuration: 'var(--dur-base)',
                  transitionTimingFunction: 'var(--ease-standard)',
                }}
              >
                <span aria-hidden className="absolute right-full top-1/2 mr-1.5 flex -translate-y-1/2">
                  {verrouillee ? (
                    <IconPadlockSquare1 size={12} data-testid="cadenas-de-valeur" />
                  ) : (
                    <IconCheck size={12} className={cn('transition-opacity', etat === 'faite' ? 'opacity-100' : 'opacity-0')} />
                  )}
                </span>
                <span className="truncate" style={{ maxWidth: LIBELLE_MAX }}>
                  {valeur.libelle}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </ZoneQuiDefile>
  );
}

/**
 * UN EN-TETE DANS LA RANGEE 3 : la piste (mode `flux`) ou les etiquettes (mode
 * `selection`), sans titre, avec le fondu du dock au repos (`useEveil`).
 * INERTE quand l'ecran ne donne pas de quoi ecrire ou pose une raison : la
 * bande la porte au survol (G16), les valeurs restent lisibles. Il ne GRANDIT
 * pas : il prend sa largeur, et RETRECIT avec les autres quand la place manque
 * (`min-w-0`), sa zone defilant alors (`ZoneQuiDefile`).
 */
export function EtapesDeLaFiche({ etapes }: { etapes: EtapesDuGabarit }): JSX.Element | null {
  const eveil = useEveil();
  if (etapes.valeurs.length === 0) return null;
  const courante = indexDeLaValeur(etapes.valeurs, etapes.valeur);
  const raison = raisonDesEtapes(etapes);
  const onChoisir = etapes.onChoisir;
  const choisir =
    raison != null || onChoisir == null
      ? null
      : (index: number): void => {
          const valeur = etapes.valeurs[index];
          if (valeur !== undefined) onChoisir(valeur.id);
        };
  /* LE VERROU : les valeurs que la condition ferme, hors la courante. Une piste deja inerte n'en dit rien de plus. */
  const verrouilles = new Set(etapes.verrou?.ids ?? []);
  const verrou: VerrouDeLaPiste | undefined =
    etapes.verrou === undefined || verrouilles.size === 0
      ? undefined
      : { verrouillee: (index) => verrouilles.has(etapes.valeurs[index]?.id ?? ''), raison: (index) => etapes.verrou?.raisons?.[etapes.valeurs[index]?.id ?? ''] ?? etapes.verrou?.raison ?? '' };
  const Mode = etapes.mode === 'selection' ? SelectionDeFiche : PisteDEtapes;
  return (
    <div
      {...eveil.gestes}
      data-testid="etapes-de-la-fiche"
      {...(raison == null ? {} : { title: `${etapes.libelle} : ${raison}` })}
      className={cn('flex min-w-0', eveil.fondu)}
    >
      <Mode libelle={etapes.libelle} valeurs={etapes.valeurs} courante={courante} choisir={choisir} {...(verrou === undefined ? {} : { verrou })} />
    </div>
  );
}

/**
 * LES EN-TETES DE LA FICHE, cote a cote dans l'ordre de la porte : alignes a
 * gauche juste apres la sortie (`ml-3`), un petit espace entre eux (`gap-3`),
 * dans la place libre qu'ils se PARTAGENT (`flex-[1_1_auto]` : une base au
 * contenu, car avec `flex-1` (base 0) le groupe ne passerait jamais a la ligne
 * quand les gestes visibles remplissent la rangee, il se ferait ecraser ;
 * `min-w-0` le laisse se tronquer s'il depasse l'ecran) ;
 * chacun garde son mode, la largeur de ses cases, sa valeur courante
 * visible. Un en-tete sans valeur ne se rend pas ; aucun : rien du tout. Rien
 * sur un brouillon : c'est `ScreenLayout` qui ne les monte pas.
 */
export function EnTetesDeLaFiche({ etapes }: { etapes: readonly EtapesDuGabarit[] }): JSX.Element | null {
  const montres = etapes.filter((enTete) => enTete.valeurs.length > 0);
  if (montres.length === 0) return null;
  return (
    <div data-testid="en-tetes-de-la-fiche" className="ml-3 mr-2 flex min-w-0 flex-[1_1_auto] items-center justify-start gap-3">
      {montres.map((enTete) => (
        <EtapesDeLaFiche key={enTete.champ} etapes={enTete} />
      ))}
    </div>
  );
}
